import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Input,
  Label,
} from "reactstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import { GetData, PutData } from "../../services/api";
import Swal from 'sweetalert2';
import ManageContactListsModal from "../../components/ContactLists/ManageContactListsModal";
import "./details.css";

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [manageListsOpen, setManageListsOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    address: "",
  });

  useEffect(() => {
    const fetchLead = async () => {
      try {
        setLoading(true);
        const response = await GetData(`/customers/${id}`);
        console.log("Response from API:", response);
        
        // Manejar diferentes estructuras de respuesta
        let leadData = null;
        if (response && response.data) {
          leadData = response.data;
        } else if (response && typeof response === 'object' && response.id) {
          leadData = response;
        } else if (Array.isArray(response) && response.length > 0) {
          leadData = response[0];
        }

        if (!leadData || !leadData.id) {
          console.error("No lead data found in response:", response);
          setLead(null);
          return;
        }

        setLead(leadData);
        setFormData({
          name: leadData.name || "",
          lastName: leadData.lastName || "",
          email: leadData.email || "",
          phone: leadData.phone || "",
          city: leadData.city || "",
          address: leadData.address || "",
        });
      } catch (error) {
        console.error("Error fetching lead:", error);
        if (error.response && error.response.status === 404) {
          setLead(null);
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLead();
    }
  }, [id, refreshTick]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        name: formData.name,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city || "",
        address: formData.address || "",
      };

      await PutData(`/customers/${id}`, updateData);
      
      // Actualizar el estado del lead con los nuevos datos
      setLead((prevLead) => ({
        ...prevLead,
        ...updateData,
      }));

      setEditMode(false);
      
      // Mostrar mensaje de éxito
      Swal.fire({
        title: "¡Éxito!",
        text: "Lead actualizado correctamente",
        icon: "success",
        confirmButtonColor: "#E9553E",
        confirmButtonText: "Aceptar"
      });
    } catch (error) {
      console.error("Error saving lead:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Error al guardar los cambios. Por favor, intenta de nuevo.",
        icon: "error",
        confirmButtonColor: "#E9553E",
        confirmButtonText: "Aceptar"
      });
    }
  };

  const handleDelete = async () => {
    try {
      // TODO: Implementar eliminación del lead cuando exista el endpoint
      console.log("Eliminando lead:", id);
      setDeleteModal(false);
      
      // Por ahora mostrar mensaje de que está en desarrollo
      Swal.fire({
        title: "En desarrollo",
        text: "La funcionalidad de eliminación estará disponible próximamente",
        icon: "info",
        confirmButtonColor: "#E9553E",
        confirmButtonText: "Aceptar"
      });
      
      // Cuando esté implementado, descomentar:
      // await DeleteData(`/customers/${id}`);
      // navigate("/leads");
    } catch (error) {
      console.error("Error deleting lead:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Error al eliminar el lead. Por favor, intenta de nuevo.",
        icon: "error",
        confirmButtonColor: "#E9553E",
        confirmButtonText: "Aceptar"
      });
    }
  };

  if (loading) {
    return (
      <Loader message="Cargando información del lead..." fullScreen={true} size={200} />
    );
  }

  if (!lead) {
    return (
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="IHubG" breadcrumbItem="Leads" />
          <Card>
            <CardBody className="text-center py-5">
              <h5 className="text-muted">Lead no encontrado</h5>
              <Link to="/leads">
                <Button color="primary" className="mt-3" style={{ backgroundColor: "#E9553E", borderColor: "#E9553E" }}>
                  Volver a Leads
                </Button>
              </Link>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  const contactLists = lead.contactLists || [];

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs
          title="IHubG"
          breadcrumbItem={
            <>
              <Link to="/leads" style={{ color: "inherit", textDecoration: "none" }}>
                Leads
              </Link>{" "}
              / {lead.name} {lead.lastName}
            </>
          }
        />

        {/* Header con acciones */}
        <Row className="mb-4">
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <h4 className="mb-1">
                      {lead.name} {lead.lastName}
                    </h4>
                    <p className="text-muted mb-0">
                      <i className="ri-mail-line me-1"></i>
                      {lead.email} • <i className="ri-phone-line me-1"></i>
                      {lead.phone}
                    </p>
                  </div>
                  <div className="d-flex gap-2 mt-3 mt-md-0">
                    {!editMode ? (
                      <>
                        <Button
                          color="primary"
                          onClick={() => setEditMode(true)}
                          style={{ backgroundColor: "#E9553E", borderColor: "#E9553E" }}
                        >
                          <i className="ri-edit-line me-1"></i>
                          Editar
                        </Button>
                        <Button
                          color="danger"
                          outline
                          onClick={() => setDeleteModal(true)}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          Eliminar
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          color="success"
                          onClick={handleSave}
                          style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
                        >
                          <i className="ri-check-line me-1"></i>
                          Guardar
                        </Button>
                        <Button
                          color="secondary"
                          outline
                          onClick={() => {
                            setEditMode(false);
                            // Restaurar datos originales
                            setFormData({
                              name: lead.name || "",
                              lastName: lead.lastName || "",
                              email: lead.email || "",
                              phone: lead.phone || "",
                              city: lead.city || "",
                              address: lead.address || "",
                            });
                          }}
                        >
                          <i className="ri-close-line me-1"></i>
                          Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Información Principal */}
          <Col lg={8}>
            <Card className="mb-4">
              <CardHeader>
                <h5 className="mb-0">
                  <i className="ri-user-line me-2"></i>
                  Información Personal
                </h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6} className="mb-3">
                    <Label className="form-label text-muted small">Nombre</Label>
                    {editMode ? (
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Nombre"
                      />
                    ) : (
                      <p className="mb-0 fw-medium">{lead.name || "-"}</p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label className="form-label text-muted small">Apellidos</Label>
                    {editMode ? (
                      <Input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Apellidos"
                      />
                    ) : (
                      <p className="mb-0 fw-medium">{lead.lastName || "-"}</p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label className="form-label text-muted small">Email</Label>
                    {editMode ? (
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email"
                      />
                    ) : (
                      <p className="mb-0">
                        <a href={`mailto:${lead.email}`} className="text-decoration-none">
                          {lead.email || "-"}
                        </a>
                      </p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label className="form-label text-muted small">Teléfono</Label>
                    {editMode ? (
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Teléfono"
                      />
                    ) : (
                      <p className="mb-0">
                        <a href={`tel:${lead.phone}`} className="text-decoration-none">
                          {lead.phone || "-"}
                        </a>
                      </p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label className="form-label text-muted small">Ciudad</Label>
                    {editMode ? (
                      <Input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Ciudad"
                      />
                    ) : (
                      <p className="mb-0 fw-medium">{lead.city || "-"}</p>
                    )}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label className="form-label text-muted small">Dirección</Label>
                    {editMode ? (
                      <Input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Dirección"
                      />
                    ) : (
                      <p className="mb-0 fw-medium">{lead.address || "-"}</p>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Listas de Contacto */}
            <Card className="contact-lists-card">
              <CardHeader
                style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #dee2e6" }}
                className="d-flex justify-content-between align-items-center"
              >
                <h5 className="mb-0">
                  <i className="ri-list-check me-2"></i>
                  Listas de Contacto
                </h5>
                <Button
                  color="primary"
                  size="sm"
                  outline
                  onClick={() => setManageListsOpen(true)}
                >
                  <i className="ri-list-settings-line me-1"></i>
                  Gestionar listas
                </Button>
              </CardHeader>
              <CardBody>
                {contactLists.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {contactLists.map((list, idx) => (
                      <Badge
                        key={list.id || idx}
                        className="contact-list-badge p-2"
                      >
                        <i className="ri-list-check me-1"></i>
                        {list.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">
                    <i className="ri-information-line me-1"></i>
                    Este lead no pertenece a ninguna lista de contacto
                  </p>
                )}
              </CardBody>
            </Card>

            <ManageContactListsModal
              isOpen={manageListsOpen}
              toggle={() => setManageListsOpen(false)}
              customerId={id}
              currentLists={contactLists}
              onDone={() => setRefreshTick((t) => t + 1)}
            />
          </Col>

          {/* Sidebar con información adicional */}
          <Col lg={4}>
            <Card className="mb-4">
              <CardHeader>
                <h5 className="mb-0">
                  <i className="ri-information-line me-2"></i>
                  Información Adicional
                </h5>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <Label className="form-label text-muted small">Fecha de Creación</Label>
                  <p className="mb-0 fw-medium">
                    {lead.createdAt ? moment(lead.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                  </p>
                </div>
                <div className="mb-3">
                  <Label className="form-label text-muted small">Última Actualización</Label>
                  <p className="mb-0 fw-medium">
                    {lead.updatedAt ? moment(lead.updatedAt).format("DD/MM/YYYY HH:mm") : "-"}
                  </p>
                </div>
                {lead.createdBy && (
                  <div className="mb-3">
                    <Label className="form-label text-muted small">Creado por</Label>
                    <p className="mb-0 fw-medium">{lead.createdBy}</p>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Acciones Rápidas */}
            <Card>
              <CardHeader>
                <h5 className="mb-0">
                  <i className="ri-flashlight-line me-2"></i>
                  Acciones Rápidas
                </h5>
              </CardHeader>
              <CardBody>
                <div className="d-grid gap-2">
                  <Button
                    color="primary"
                    outline
                    block
                    style={{ borderColor: "#E9553E", color: "#E9553E" }}
                    onClick={() => window.open(`mailto:${lead.email}`, "_blank")}
                  >
                    <i className="ri-mail-send-line me-1"></i>
                    Enviar Email
                  </Button>
                  <Button
                    color="primary"
                    outline
                    block
                    style={{ borderColor: "#E9553E", color: "#E9553E" }}
                    onClick={() => window.open(`tel:${lead.phone}`, "_blank")}
                  >
                    <i className="ri-phone-line me-1"></i>
                    Llamar
                  </Button>
                  <Button
                    color="primary"
                    outline
                    block
                    style={{ borderColor: "#E9553E", color: "#E9553E" }}
                  >
                    <i className="ri-calendar-line me-1"></i>
                    Agregar a Campaña
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal de Eliminación */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
          <ModalHeader toggle={() => setDeleteModal(false)}>
            <i className="ri-alert-line me-2 text-danger"></i>
            Confirmar Eliminación
          </ModalHeader>
          <ModalBody>
            <p>¿Estás seguro de que deseas eliminar este lead?</p>
            <p className="text-muted small mb-0">
              Esta acción no se puede deshacer. Se eliminará toda la información asociada a este lead.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setDeleteModal(false)}>
              Cancelar
            </Button>
            <Button
              color="danger"
              onClick={handleDelete}
              style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
            >
              <i className="ri-delete-bin-line me-1"></i>
              Eliminar
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default LeadDetails;
